/**
 * Date and Time Utilities for EcoDeal
 * 
 * Handles date/time formatting and conversions for orders and reservations
 */

/**
 * Convert time string (HH:MM) to full timestamp for today or tomorrow
 * @param timeString - Time in HH:MM format (e.g., "09:00")
 * @param isNextDay - Whether the time is for next day (default: false)
 * @returns ISO timestamp string
 */
export function timeToTimestamp(timeString: string, isNextDay: boolean = false): string {
  if (!timeString || !timeString.match(/^\d{2}:\d{2}$/)) {
    throw new Error(`Invalid time format: ${timeString}. Expected HH:MM format.`);
  }

  const [hours, minutes] = timeString.split(':').map(Number);
  
  if (hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
    throw new Error(`Invalid time values: ${timeString}`);
  }

  const now = new Date();
  const targetDate = new Date(now);
  
  if (isNextDay) {
    targetDate.setDate(targetDate.getDate() + 1);
  }
  
  targetDate.setHours(hours, minutes, 0, 0);
  
  return targetDate.toISOString();
}

/**
 * Convert timestamp to time string
 * @param timestamp - ISO timestamp string
 * @returns Time in HH:MM format
 */
export function timestampToTime(timestamp: string): string {
  try {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('ko-KR', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: false 
    });
  } catch (error) {
    console.error('Invalid timestamp:', timestamp);
    return timestamp; // Return original if conversion fails
  }
}

/**
 * Format time string for display
 * @param timeString - Time in HH:MM format or ISO timestamp
 * @returns Formatted time string
 */
export function formatTime(timeString: string): string {
  // If it's already in HH:MM format, return as is
  if (timeString.match(/^\d{2}:\d{2}$/)) {
    return timeString;
  }
  
  // If it's a timestamp, convert to time
  return timestampToTime(timeString);
}

/**
 * Get pickup time based on order type and desired time
 * @param orderType - Type of order
 * @param desiredPickupTime - Desired pickup time (HH:MM format)
 * @returns ISO timestamp string or undefined
 */
export function getPickupTimestamp(
  orderType: 'DEADLINE_DISCOUNT' | 'RESERVATION_DISCOUNT' | 'REGULAR_ORDER',
  desiredPickupTime?: string
): string | undefined {
  if (!desiredPickupTime) {
    return undefined;
  }

  switch (orderType) {
    case 'RESERVATION_DISCOUNT':
      // Reservation discount is for next day pickup
      return timeToTimestamp(desiredPickupTime, true);
    
    case 'DEADLINE_DISCOUNT':
    case 'REGULAR_ORDER':
      // Same day pickup
      return timeToTimestamp(desiredPickupTime, false);
    
    default:
      return undefined;
  }
}

/**
 * Validate pickup time format
 * @param timeString - Time string to validate
 * @returns true if valid, false otherwise
 */
export function isValidTimeFormat(timeString: string): boolean {
  return /^\d{2}:\d{2}$/.test(timeString);
}

/**
 * Get current time in HH:MM format
 * @returns Current time as HH:MM string
 */
export function getCurrentTime(): string {
  const now = new Date();
  return now.toLocaleTimeString('ko-KR', { 
    hour: '2-digit', 
    minute: '2-digit',
    hour12: false 
  });
}

/**
 * Add hours to timestamp
 * @param timestamp - ISO timestamp string
 * @param hours - Number of hours to add
 * @returns New ISO timestamp string
 */
export function addHours(timestamp: string, hours: number): string {
  const date = new Date(timestamp);
  date.setHours(date.getHours() + hours);
  return date.toISOString();
}

/**
 * 한국 시간(KST) 기준 현재 날짜를 YYYY-MM-DD 형식으로 반환
 * UTC와 KST 시간차로 인한 날짜 오류를 방지
 * @returns {string} 한국 시간 기준 날짜 (예: "2025-08-10")
 */
export function getKoreanDate(): string {
  const now = new Date();
  
  // 한국 시간대(Asia/Seoul)로 변환
  const koreanTime = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Seoul" }));
  
  // YYYY-MM-DD 형식으로 반환
  const year = koreanTime.getFullYear();
  const month = String(koreanTime.getMonth() + 1).padStart(2, '0');
  const day = String(koreanTime.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * 한국 시간 기준 내일 날짜를 YYYY-MM-DD 형식으로 반환
 * @returns {string} 한국 시간 기준 내일 날짜
 */
export function getKoreanTomorrowDate(): string {
  const now = new Date();
  
  // 한국 시간대로 변환 후 하루 추가
  const koreanTime = new Date(now.toLocaleString("en-US", { timeZone: "Asia/Seoul" }));
  koreanTime.setDate(koreanTime.getDate() + 1);
  
  const year = koreanTime.getFullYear();
  const month = String(koreanTime.getMonth() + 1).padStart(2, '0');
  const day = String(koreanTime.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}

/**
 * 한국 시간 기준 특정 날짜로부터 N일 후 날짜를 반환
 * @param baseDate 기준 날짜 (YYYY-MM-DD)
 * @param daysToAdd 추가할 일 수
 * @returns {string} 계산된 날짜 (YYYY-MM-DD)
 */
export function addDaysToKoreanDate(baseDate: string, daysToAdd: number): string {
  const date = new Date(baseDate + 'T00:00:00+09:00'); // KST 타임존으로 파싱
  date.setDate(date.getDate() + daysToAdd);
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  
  return `${year}-${month}-${day}`;
}